// src/pages/fleet/FleetReportsPage.tsx — fleet analytics & reports.
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getFleetReports, getTripReport, getDeviceHealth, getAlertReport, getTripPerformance } from '../../services/fleetApi';
import type { TripReportRow, TripPerformanceSummary } from '../../services/fleetApi';
import { Modal, ReportTable, RCol, Tag, fmtDT, fmtDur, fmtKm, useReport } from './reports/reportKit';
import { ALERT_COLS, DEVICE_COLS, TRIP_COLS, DEVIATION_COLS, TIME_COLS, fmtExtra } from './reports/columns';
import Icon, { IconName } from './Icon';
import { P } from './fleetStyles';
import { css } from '../../utils/css';
import type { FleetSourceStatus, ISODate } from '../../types';

// Shape of GET /api/fleet/reports (backend fleetController.getFleetReports).
interface FleetReportSummary {
  totalTrips: number;
  completed: number;
  active: number;
  pending: number;
  cancelled: number;
  totalDistanceKm: number;
  totalBags: number;
  totalAlerts: number;
  totalVehicles: number;
  offlineVehicles: number;
  delayedVehicles: number;
  gpsGapVehicles: number;
  movingVehicles: number;
  unhealthySources: number;
}
interface StatusSlice { name: string; value: number }
interface TypeCount { type: string; count: number }
interface VehicleReportRow {
  id: string;
  vehicleNumber: string;
  driverName: string;
  status: string;
  trips: number;
  distanceKm: number;
  bags: number;
  healthStatus: string;
  healthLabel: string;
  gpsAgeMinutes: number | null;
  ingestAgeMinutes: number | null;
  dataDelayMinutes: number | null;
  openIssueCount: number;
  lastIssueType: string | null;
  lastIssueMessage: string | null;
}
interface FleetReports {
  summary: FleetReportSummary;
  tripsByStatus: StatusSlice[];
  alertsByType: TypeCount[];
  openIssuesByType: TypeCount[];
  sourceStatuses: FleetSourceStatus[];
  perVehicle: VehicleReportRow[];
}

// What each summary card opens in its pop-up.
type CardKind = 'deviation' | 'time' | 'trips' | 'completed' | 'active' | 'offline' | 'delayed' | 'gpsgap' | 'distance' | 'bags' | 'alerts' | 'sources';

// Report modules listed on the Overview (same routes as the sidebar submenu).
const MODULES: { to: string; icon: IconName; tint: string; title: string; text: string }[] = [
  { to: '/fleet/reports/performance', icon: 'clock', tint: '#34C759', title: 'Trip Performance', text: 'Route deviation and time vs estimate' },
  { to: '/fleet/reports/trips',     icon: 'list',    tint: '#FF9500', title: 'Trip Report',      text: 'Trips with timings, distance and consignment' },
  { to: '/fleet/reports/vehicles',  icon: 'truck',   tint: '#007AFF', title: 'Vehicle Summary',  text: 'Distance, running and halt time per truck' },
  { to: '/fleet/reports/stoppages', icon: 'pin',     tint: '#FF3B30', title: 'Stoppage Report',  text: 'Where trucks stopped and for how long' },
  { to: '/fleet/reports/overspeed', icon: 'chart',   tint: '#AF52DE', title: 'Overspeed Report', text: 'Stretches driven above the speed limit' },
  { to: '/fleet/reports/alerts',    icon: 'bell',    tint: '#FF2D55', title: 'Alert Report',     text: 'GPS, route and device alerts' },
  { to: '/fleet/reports/devices',   icon: 'antenna', tint: '#30B0C7', title: 'Device Health',    text: 'Live state of every GPS device' },
];

const KPI_TITLES: Partial<Record<CardKind, string>> = {
  trips: 'All Trips', completed: 'Completed Trips', active: 'Active Trips', offline: 'Offline Devices',
  delayed: 'Delayed GPS Data', gpsgap: 'GPS Gap Vehicles', distance: 'Distance by Trip', bags: 'Bags Moved by Trip',
  alerts: 'All Alerts', sources: 'GPS Data Sources', deviation: 'Route Deviation by Trip', time: 'Time Taken vs Estimate',
};

const STATUS_COLORS: Record<string, string> = { Completed: '#6B7280', Active: '#22A559', Pending: '#E8941A', Cancelled: '#DC3545' };

export default function FleetReportsPage() {
  const [data, setData] = useState<FleetReports | null>(null);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState<{ kind: CardKind; title: string } | null>(null);
  // ?card=sources (dashboard "Source Issues") opens that pop-up straight away.
  useEffect(() => {
    const c = params.get('card');
    const card = c && KPI_TITLES[c as CardKind];
    if (card) { setOpen({ kind: c as CardKind, title: card }); setParams({}, { replace: true }); }
  }, [params, setParams]);
  const [perf, setPerf] = useState<TripPerformanceSummary | null>(null);
  useEffect(() => { getTripPerformance({ all: 1 }).then(r => setPerf(r.data.summary)).catch(() => {}); }, []);

  useEffect(() => { getFleetReports<FleetReports>().then(r => setData(r.data)).catch(() => {}); }, []);

  const exportCsv = () => {
    if (!data) return;
    const rows: (string | number)[][] = [
      ['Vehicle', 'Driver', 'Status', 'Health', 'Trips', 'Distance (km)', 'Bags', 'GPS Age (min)', 'Data Delay (min)', 'Open Issues', 'Last Issue'],
    ];
    data.perVehicle.forEach(v => rows.push([
      v.vehicleNumber,
      v.driverName,
      v.status,
      v.healthLabel,
      v.trips,
      v.distanceKm,
      v.bags,
      v.gpsAgeMinutes ?? '',
      v.dataDelayMinutes ?? '',
      v.openIssueCount ?? 0,
      v.lastIssueType || '',
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'fleet-report.csv'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const s: Partial<FleetReportSummary> = data?.summary || {};
  const KPI: { kind: CardKind; icon: string; label: string; value: React.ReactNode; c: string; popTitle: string }[] = [
    { kind: 'trips',     icon: '🚚', label: 'Total Trips', value: s.totalTrips ?? '—', c: '#1560BD', popTitle: 'All Trips' },
    { kind: 'completed', icon: '✅', label: 'Completed', value: s.completed ?? '—', c: '#22A559', popTitle: 'Completed Trips' },
    { kind: 'active',    icon: '📍', label: 'Active', value: s.active ?? '—', c: '#E8941A', popTitle: 'Active Trips' },
    { kind: 'offline',   icon: '📴', label: 'Offline', value: s.offlineVehicles ?? '—', c: '#6B7280', popTitle: 'Offline Devices' },
    { kind: 'delayed',   icon: '⏱️', label: 'Delayed', value: s.delayedVehicles ?? '—', c: '#B42318', popTitle: 'Delayed GPS Data' },
    { kind: 'gpsgap',    icon: '📡', label: 'GPS Gap', value: s.gpsGapVehicles ?? '—', c: '#7C3AED', popTitle: 'GPS Gap Vehicles' },
    { kind: 'distance',  icon: '🛣️', label: 'Total Distance', value: s.totalDistanceKm != null ? `${s.totalDistanceKm} km` : '—', c: '#7C3AED', popTitle: 'Distance by Trip' },
    { kind: 'bags',      icon: '📦', label: 'Bags Moved', value: s.totalBags ?? '—', c: '#0EA5E9', popTitle: 'Bags Moved by Trip' },
    { kind: 'alerts',    icon: '🚨', label: 'Total Alerts', value: s.totalAlerts ?? '—', c: '#DC3545', popTitle: 'All Alerts' },
    { kind: 'sources',   icon: '🌐', label: 'Source Issues', value: s.unhealthySources ?? '—', c: '#B42318', popTitle: 'GPS Data Sources' },
  ];
  const tripsByStatus = (data?.tripsByStatus || []).filter(d => d.value > 0);
  const alertData = (data?.openIssuesByType?.length ? data.openIssuesByType : data?.alertsByType || []).map(a => ({ name: a.type.replace(/_/g, ' '), value: a.count }));
  const perVeh = data?.perVehicle || [];
  const sourceStatuses = data?.sourceStatuses || [];

  return (
    <div style={P.page}>
      <style>{`.fx-kpi:hover { transform: translateY(-2px); box-shadow: var(--fx-shadow-lg) !important; }
        .fx-kpi .fx-kpi-go { opacity: .35; } .fx-kpi:hover .fx-kpi-go { opacity: 1; transform: translate(2px, -50%); }
        .fx-kpi:focus-visible { outline: 2px solid var(--fx-accent); outline-offset: 2px; }`}</style>
      <div style={P.head}>
        <div><h1 style={P.h1}>📊 Fleet Reports</h1><p style={P.sub}>Click any card for its full report · or open a report module below</p></div>
        <button style={P.btnPrimary} onClick={exportCsv}>⬇ Export CSV</button>
      </div>

      {/* KPIs */}
      <div style={S.kpis}>
        {KPI.map(k => (
          <button key={k.label} type="button" className="fx-kpi" onClick={() => setOpen({ kind: k.kind, title: k.popTitle })}
            style={{ ...S.kpi, borderTop: `3px solid ${k.c}` }} aria-label={`${k.label}: open report`}>
            <div style={S.kpiIcon}>{k.icon}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...S.kpiVal, color: k.c }}>{k.value}</div>
              <div style={S.kpiLbl}>{k.label}</div>
            </div>
            <span className="fx-kpi-go" style={S.kpiGo}>›</span>
          </button>
        ))}
      </div>

      {/* Key insights: route deviation + time vs estimate */}
      <div style={S.cardT}>Key Insights</div>
      <div style={S.insights}>
        <button type="button" className="fx-kpi" style={{ ...S.insight, borderTop: '3px solid #FF3B30' }}
          onClick={() => setOpen({ kind: 'deviation', title: 'Route Deviation by Trip' })} aria-label="Route deviation: open report">
          <div style={S.insHead}>
            <span style={{ ...S.insIcon, background: 'linear-gradient(180deg,#FF6B61 0%,#FF3B30 100%)' }}><Icon name="pin" size={17} stroke={2} /></span>
            <div style={{ flex: 1 }}>
              <div style={S.insTitle}>Route Deviation</div>
              <div style={S.insSub}>Truck more than {perf?.deviationThresholdKm ?? 5} km away from its planned route</div>
            </div>
            <span className="fx-kpi-go" style={S.insGo}>View ›</span>
          </div>
          <div style={S.insBig}>
            <span style={{ ...S.insNum, color: perf?.deviationEvents ? '#FF3B30' : 'var(--fx-green)' }}>{perf ? perf.deviationEvents : '—'}</span>
            <span style={S.insNumLbl}>times off route{perf ? ` · in ${perf.deviationTrips} of ${perf.trips} trips` : ''}</span>
          </div>
          <div style={S.insGrid}>
            <Metric label="Time off route" value={perf ? fmtDur(perf.deviationMin) : '—'} />
            <Metric label="Farthest off route" value={perf?.maxDeviationKm ? fmtKm(perf.maxDeviationKm) : '—'} />
            <Metric label="Off route right now" value={perf ? perf.deviatingNow : '—'} warn={!!perf?.deviatingNow} />
          </div>
        </button>

        <button type="button" className="fx-kpi" style={{ ...S.insight, borderTop: '3px solid #0A84FF' }}
          onClick={() => setOpen({ kind: 'time', title: 'Time Taken vs Estimate' })} aria-label="Time vs estimate: open report">
          <div style={S.insHead}>
            <span style={{ ...S.insIcon, background: 'linear-gradient(180deg,#4DA3FF 0%,#0A84FF 100%)' }}><Icon name="clock" size={17} stroke={2} /></span>
            <div style={{ flex: 1 }}>
              <div style={S.insTitle}>Time vs Estimate</div>
              <div style={S.insSub}>Actual trip time against the estimated start → end (±{perf?.graceMin ?? 30} min counts as on time)</div>
            </div>
            <span className="fx-kpi-go" style={S.insGo}>View ›</span>
          </div>
          <div style={S.insBig}>
            <span style={{ ...S.insNum, color: perf?.onTimePct == null ? 'var(--fx-text-3)' : perf.onTimePct >= 80 ? 'var(--fx-green)' : perf.onTimePct >= 50 ? '#FF9500' : '#FF3B30' }}>
              {perf?.onTimePct == null ? '—' : `${perf.onTimePct}%`}
            </span>
            <span style={S.insNumLbl}>{perf?.finished ? `on time · ${perf.finished} completed trip${perf.finished === 1 ? '' : 's'} with an estimate` : 'no completed trip with an estimate yet'}</span>
          </div>
          <div style={S.insGrid}>
            <Metric label="Late trips" value={perf ? perf.late : '—'} warn={!!perf?.late} />
            <Metric label="Avg. extra time" value={perf ? fmtExtra(perf.avgExtraMin) : '—'} warn={(perf?.avgExtraMin ?? 0) > 30} />
            <Metric label="Estimated → actual" value={perf?.totalEstimatedMin ? `${fmtDur(perf.totalEstimatedMin).replace(' 00m', '')} → ${fmtDur(perf.totalActualMin).replace(' 00m', '')}` : '—'} />
            <Metric label="Running late now" value={perf ? perf.runningLate : '—'} warn={!!perf?.runningLate} />
          </div>
        </button>
      </div>

      {/* Report modules */}
      <div style={S.cardT}>Report Modules</div>
      <div style={S.modules}>
        {MODULES.map((m) => (
          <Link key={m.to} to={m.to} className="fx-kpi" style={S.module}>
            <span style={{ ...S.modIcon, background: `linear-gradient(180deg, ${m.tint}CC 0%, ${m.tint} 100%)` }}><Icon name={m.icon} size={17} stroke={2} /></span>
            <span style={{ minWidth: 0 }}>
              <span style={S.modTitle}>{m.title}</span>
              <span style={S.modText}>{m.text}</span>
            </span>
          </Link>
        ))}
      </div>

      {open && (
        <Modal title={open.title} subtitle="Search, sort and download as Excel, CSV or PDF" onClose={() => setOpen(null)}>
          <CardReport kind={open.kind} title={open.title} sources={sourceStatuses} />
        </Modal>
      )}

      {/* Charts */}
      <div style={S.charts}>
        <div style={P.card}>
          <div style={S.cardT}>Trips by Status</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={tripsByStatus.length ? tripsByStatus : [{ name: 'None', value: 1 }]} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {(tripsByStatus.length ? tripsByStatus : [{ name: 'None' }]).map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.name] || '#8E8E9340'} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div style={S.legend}>{tripsByStatus.map(d => <span key={d.name} style={S.legItem}><span style={{ ...S.legDot, background: STATUS_COLORS[d.name] }} />{d.name} {d.value}</span>)}</div>
        </div>

        <div style={P.card}>
          <div style={S.cardT}>Alerts by Type</div>
          {alertData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={alertData} layout="vertical" margin={{ top: 6, right: 14, left: 10, bottom: 0 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#8E8E93' }} width={110} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#DC3545" radius={[0, 6, 6, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={S.noData}>No alerts ✓</div>}
        </div>

        <div style={P.card}>
          <div style={S.cardT}>Trips per Vehicle</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={perVeh} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="vehicleNumber" tick={{ fontSize: 9, fill: '#8E8E93' }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: '#8E8E93' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="trips" fill="#1560BD" radius={[6, 6, 0, 0]} barSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {sourceStatuses.length > 0 && (
        <div style={{ ...P.card, padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ ...S.cardT, padding: '14px 16px 0' }}>Data Source Status</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...P.table, minWidth: 720 }}>
              <thead><tr>{['Source', 'Status', 'Last Poll', 'Last Success', 'Failures', 'Forwarded', 'Last Error'].map(h => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
              <tbody>
                {sourceStatuses.map((src) => (
                  <tr key={src.source}>
                    <td style={P.td}><b>{src.source}</b></td>
                    <td style={P.td}><span style={{ ...P.badge, background: src.status === 'HEALTHY' ? '#22A559' : src.status === 'DEGRADED' ? '#E8941A' : '#B42318' }}>{src.status}</span></td>
                    <td style={P.td}>{fmt(src.lastPollAt)}</td>
                    <td style={P.td}>{fmt(src.lastSuccessAt)}</td>
                    <td style={P.td}>{src.consecutiveFailures ?? 0}</td>
                    <td style={P.td}>{src.lastDevicesForwarded ?? 0} / {src.lastDevicesSeen ?? 0}</td>
                    <td style={P.td}>{src.lastError || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-vehicle table */}
      <div style={{ ...P.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ ...S.cardT, padding: '14px 16px 0' }}>Per-Vehicle Performance</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...P.table, minWidth: 980 }}>
            <thead><tr>{['Vehicle', 'Driver', 'Vehicle Status', 'Health', 'Trips', 'Distance', 'Bags', 'GPS Age', 'Data Delay', 'Open Issues', 'Last Issue'].map(h => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
            <tbody>
              {perVeh.map((v, i) => (
                <tr key={i}>
                  <td style={P.td}><b>{v.vehicleNumber}</b></td>
                  <td style={P.td}>{v.driverName}</td>
                  <td style={P.td}>{v.status}</td>
                  <td style={P.td}><span style={{ ...P.badge, background: v.healthStatus === 'MOVING' ? '#22A559' : v.healthStatus === 'HALTED' ? '#E8941A' : v.healthStatus === 'GPS_GAP' ? '#7C3AED' : v.healthStatus === 'DELAYED' ? '#B42318' : '#6B7280' }}>{v.healthLabel}</span></td>
                  <td style={P.td}>{v.trips}</td>
                  <td style={P.td}>{v.distanceKm} km</td>
                  <td style={P.td}>{v.bags}</td>
                  <td style={P.td}>{v.gpsAgeMinutes != null ? `${v.gpsAgeMinutes} min` : '—'}</td>
                  <td style={P.td}>{v.dataDelayMinutes != null ? `${v.dataDelayMinutes} min` : '—'}</td>
                  <td style={P.td}>{v.openIssueCount ?? 0}</td>
                  <td style={P.td}>{v.lastIssueType ? `${v.lastIssueType.replace(/_/g, ' ')}${v.lastIssueMessage ? ` — ${v.lastIssueMessage}` : ''}` : '—'}</td>
                </tr>
              ))}
              {perVeh.length === 0 && <tr><td colSpan={11} style={P.emptyTd}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Pop-up body for a summary card ────────────────────────────────────────────
const BAGS_COLS: RCol<TripReportRow>[] = [
  ...TRIP_COLS.filter((c) => ['tripNumber', 'vehicleNumber', 'fromLocation', 'toLocation', 'status'].includes(c.key)),
  { key: 'bagsCount', label: 'Bags', align: 'right' },
  { key: 'quantityMt', label: 'Qty (MT)', align: 'right', value: (r) => r.quantityMt ?? '—', sortValue: (r) => r.quantityMt ?? -1 },
  ...TRIP_COLS.filter((c) => c.key === 'endedAt'),
];
const SOURCE_COLS: RCol<FleetSourceStatus>[] = [
  { key: 'source', label: 'Source', render: (r) => <b>{r.source}</b> },
  { key: 'status', label: 'Status', render: (r) => <Tag text={r.status} tone={r.status === 'HEALTHY' ? 'green' : r.status === 'DEGRADED' ? 'orange' : 'red'} /> },
  { key: 'lastPollAt', label: 'Last Poll', value: (r) => fmtDT(r.lastPollAt), sortValue: (r) => r.lastPollAt || '' },
  { key: 'lastSuccessAt', label: 'Last Success', value: (r) => fmtDT(r.lastSuccessAt), sortValue: (r) => r.lastSuccessAt || '' },
  { key: 'consecutiveFailures', label: 'Failures', align: 'right', value: (r) => r.consecutiveFailures ?? 0 },
  { key: 'forwarded', label: 'Forwarded / Seen', align: 'right', value: (r) => `${r.lastDevicesForwarded ?? 0} / ${r.lastDevicesSeen ?? 0}` },
  { key: 'lastError', label: 'Last Error', value: (r) => r.lastError || '—' },
];
const HEALTH_OF: Partial<Record<CardKind, string>> = { offline: 'OFFLINE', delayed: 'DELAYED', gpsgap: 'GPS_GAP' };
const STATUS_OF: Partial<Record<CardKind, string>> = { completed: 'COMPLETED', active: 'ACTIVE' };

function Metric({ label, value, warn }: { label: string; value: React.ReactNode; warn?: boolean }) {
  return (
    <div style={S.metric}>
      <div style={{ ...S.metricVal, color: warn ? '#FF3B30' : 'var(--fx-text)' }}>{value}</div>
      <div style={S.metricLbl}>{label}</div>
    </div>
  );
}

function CardReport({ kind, title, sources }: { kind: CardKind; title: string; sources: FleetSourceStatus[] }) {
  const isPerf = kind === 'deviation' || kind === 'time';
  const perf = useReport(() => (isPerf
    ? getTripPerformance({ all: 1 }).then((r) => r.data.rows.filter((x) => (kind === 'deviation' ? x.deviations > 0 : x.verdict !== 'NO_ESTIMATE')))
    : Promise.resolve([])), [kind]);
  if (isPerf) {
    return <ReportTable title={title} columns={kind === 'deviation' ? DEVIATION_COLS : TIME_COLS} rows={perf.rows} loading={perf.loading} error={perf.error}
      initialSort={kind === 'deviation' ? { key: 'deviations', dir: -1 } : { key: 'extraMin', dir: -1 }}
      onRowClick={(r) => window.open(`/fleet/trips/${r.id}`, '_blank', 'noopener')}
      emptyText={kind === 'deviation' ? 'No route deviations ✓' : 'No trips with an estimated start / end time'} />;
  }
  return <CardReportInner kind={kind} title={title} sources={sources} />;
}

function CardReportInner({ kind, title, sources }: { kind: CardKind; title: string; sources: FleetSourceStatus[] }) {
  const isTrip = ['trips', 'completed', 'active', 'distance', 'bags'].includes(kind);
  const isDevice = !!HEALTH_OF[kind];
  const trips = useReport(() => (isTrip ? getTripReport({ all: 1, status: STATUS_OF[kind] }).then((r) => r.data.rows) : Promise.resolve([])), [kind]);
  const devices = useReport(() => (isDevice ? getDeviceHealth().then((r) => r.data.rows.filter((d) => d.healthStatus === HEALTH_OF[kind])) : Promise.resolve([])), [kind]);
  const alerts = useReport(() => (kind === 'alerts' ? getAlertReport({ all: 1 }).then((r) => r.data.rows) : Promise.resolve([])), [kind]);
  const onTrip = (id: string | null | undefined) => { if (id) window.open(`/fleet/trips/${id}`, '_blank', 'noopener'); };

  if (isTrip) {
    return <ReportTable title={title} columns={kind === 'bags' ? BAGS_COLS : TRIP_COLS} rows={trips.rows} loading={trips.loading} error={trips.error}
      initialSort={kind === 'distance' ? { key: 'distanceKm', dir: -1 } : kind === 'bags' ? { key: 'bagsCount', dir: -1 } : undefined}
      onRowClick={(r) => onTrip(r.id)} emptyText="No trips" />;
  }
  if (isDevice) {
    return <ReportTable title={title} columns={DEVICE_COLS} rows={devices.rows} loading={devices.loading} error={devices.error}
      initialSort={{ key: 'gpsAgeMinutes', dir: -1 }} emptyText="No devices in this state ✓" />;
  }
  if (kind === 'alerts') {
    return <ReportTable title={title} columns={ALERT_COLS} rows={alerts.rows} loading={alerts.loading} error={alerts.error}
      onRowClick={(r) => onTrip(r.tripId)} emptyText="No alerts ✓" />;
  }
  return <ReportTable title={title} columns={SOURCE_COLS} rows={sources} rowKey={(r) => r.source} emptyText="No data sources reporting yet" />;
}

function fmt(v: ISODate | null | undefined) {
  return v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

const S = css({
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 16 },
  kpi: { position: 'relative', background: 'var(--fx-surface)', borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: 'var(--fx-shadow)', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', transition: 'transform .15s, box-shadow .15s' },
  kpiGo: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: 'var(--fx-text-3)', transition: 'opacity .15s, transform .15s' },
  insights: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(360px,1fr))', gap: 14, marginBottom: 18 },
  insight: { position: 'relative', display: 'block', width: '100%', textAlign: 'left', background: 'var(--fx-surface)', borderRadius: 16, padding: '16px 18px', boxShadow: 'var(--fx-shadow)', borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'transform .15s, box-shadow .15s' },
  insHead: { display: 'flex', alignItems: 'flex-start', gap: 11 },
  insIcon: { width: 34, height: 34, borderRadius: 10, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' },
  insTitle: { fontSize: 15, fontWeight: 700, color: 'var(--fx-text)', letterSpacing: '-0.01em' },
  insSub: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 2, lineHeight: 1.4 },
  insGo: { fontSize: 12, fontWeight: 600, color: 'var(--fx-accent)', whiteSpace: 'nowrap', transition: 'opacity .15s' },
  insBig: { display: 'flex', alignItems: 'baseline', gap: 10, margin: '14px 0 12px', flexWrap: 'wrap' },
  insNum: { fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 },
  insNumLbl: { fontSize: 12.5, color: 'var(--fx-text-2)' },
  insGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 8 },
  metric: { background: 'var(--fx-surface-2)', borderRadius: 10, padding: '8px 10px' },
  metricVal: { fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 },
  metricLbl: { fontSize: 11, color: 'var(--fx-text-2)', marginTop: 2 },
  modules: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 12, marginBottom: 18 },
  module: { display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14, boxShadow: 'var(--fx-shadow)', textDecoration: 'none', transition: 'transform .15s, box-shadow .15s' },
  modIcon: { width: 36, height: 36, borderRadius: 10, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' },
  modTitle: { display: 'block', fontSize: 13.5, fontWeight: 650, color: 'var(--fx-text)' },
  modText: { display: 'block', fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 2 },
  kpiIcon: { fontSize: 22 },
  kpiVal: { fontSize: 22, fontWeight: 800, lineHeight: 1.1 },
  kpiLbl: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 3 },
  charts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 14, marginBottom: 16 },
  cardT: { fontSize: 14, fontWeight: 700, color: P.navy, marginBottom: 10 },
  legend: { display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', marginTop: 4 },
  legItem: { fontSize: 12, color: 'var(--fx-text-2)', display: 'flex', alignItems: 'center', gap: 5 },
  legDot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  noData: { height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fx-text-3)', fontSize: 14 },
});
